import { useRouter } from 'next/router';

import type {
  GlycerinProductListBasePayload,
  GlycerinTile,
  GTMEventGlycerinDisplayProductList,
  GTMEventGlycerinDisplaySearchResult,
} from '@packages/tracking';
import { GlycerinSortMethod, useTracking, GlycerinTileType } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { ensureExhaustiveCases } from '../../utils/ensureExhaustiveCases';
import { decodeSearchHistoryMarker, decodeSuggestMarker } from '../../queryEncoding';
import { buildGlycerinFilters } from '../../utils/tracking/buildGlycerinFilters';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { useSorting } from '../../hooks/useSorting';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

export const GlycerinListingsEventsItemFragment = graphql(/* GraphQL */ `
  fragment GlycerinListingsEventsItemFragment on StylePreviewItemBase {
    styleId
    listPosition
    primaryVariationGroup {
      sku
      availability {
        ... on AvailableVariations {
          variations {
            name
          }
        }
      }
    }
  }
`);

export const GlycerinListingsEventsResultFragment = graphql(/* GraphQL */ `
  fragment GlycerinListingsEventsResultFragment on SearchProductResult {
    __typename
    summary {
      totalResultCount
    }
    activeFilters {
      __typename
      filterId
      filterName
      activeValues {
        id
        name
      }
      ... on RangeFilterSelection {
        activeRange {
          min
          max
        }
      }
    }
    sponsoredProducts {
      items {
        ...GlycerinListingsEventsItemFragment
      }
    }
    items {
      ...GlycerinListingsEventsItemFragment
      variationGroups {
        groupName
      }
    }
    ...UseDisplayModeFragment
    ...UseSortingFragment
    ... on CategoryResult {
      categories {
        name
        isSelectedExact
      }
      breadcrumbs {
        name
      }
    }
    ... on SuccessfulSearchResult {
      recoveryStrategy {
        __typename
        ... on DeepQuery {
          alternateQuery
        }
        ... on QueryReduction {
          usedQuery
        }
      }
    }
  }
`);

/**
 * Maps the sorting method to its corresponding glycerin enum value.
 * Possible methods: relevance,price-asc,price-desc,novelty,brand-asc,brand-desc,ratings
 */
const mapSortingMethod = (method: string): GlycerinSortMethod => {
  switch (method) {
    case 'price-asc':
      return GlycerinSortMethod.PRICEASCENDING;
    case 'price-desc':
      return GlycerinSortMethod.PRICEDESCENDING;
    case 'ratings':
      return GlycerinSortMethod.RATING;
    case 'relevance':
      return GlycerinSortMethod.POPULARITY;
    default:
      return GlycerinSortMethod.OTHER;
  }
};

export type UseGlycerinListingsEventsProps = {
  maskedResult: FragmentType<typeof GlycerinListingsEventsResultFragment>;
  pageNumber: number;
};

export const useGlycerinListingsEvents = ({
  maskedResult,
  pageNumber,
}: UseGlycerinListingsEventsProps) => {
  const result = getFragmentData(GlycerinListingsEventsResultFragment, maskedResult);

  const dispatchGTMEvent = useTracking();
  const { query: urlQuery } = useRouter();
  const { displayMode } = useDisplayMode(result);
  const { sortingKey } = useSorting(result);
  const searchquery = useOriginalSearchQuery();

  const historyMarker = decodeSearchHistoryMarker(urlQuery);
  const suggestMarker = decodeSuggestMarker(urlQuery);

  const mapToStandardTile = (
    maskedItem: FragmentType<typeof GlycerinListingsEventsItemFragment>,
    variationCount: number,
  ) => {
    const item = getFragmentData(GlycerinListingsEventsItemFragment, maskedItem);

    const hasOnlySingleVariationAndSize =
      variationCount === 1 &&
      'variations' in item.primaryVariationGroup.availability &&
      item.primaryVariationGroup.availability.variations.length === 1;

    // primaryVariationGroup.articleNumber is missing the promo code, so we need to extract it from the sku
    const articleNumberWithPromo = item.primaryVariationGroup.sku.split('-')[0];

    return {
      productId: item.styleId,
      articleId: articleNumberWithPromo,
      skuId: hasOnlySingleVariationAndSize ? item.primaryVariationGroup.sku : undefined,
      listPosition: item.listPosition || undefined,
      tileType: GlycerinTileType.PRODUCT,
    };
  };

  const dispatchGlycerinListingsEvent = () => {
    const sponsoredProductTiles =
      result.sponsoredProducts?.items.map(
        (maskedItem): GlycerinTile => ({
          ...mapToStandardTile(maskedItem, 1), // sponsored products always show a single variation group
          adId: 'criteo',
        }),
      ) ?? [];

    const regularProductTiles = result.items.map(
      (maskedItem): GlycerinTile =>
        mapToStandardTile(maskedItem, maskedItem.variationGroups.length),
    );

    // map selected filters
    const filters = buildGlycerinFilters(
      result.activeFilters.map((filter) => ({
        id: filter.filterId,
        valueIds: filter.activeValues.map((x) => x.id),
        range:
          'activeRange' in filter && filter.activeRange
            ? [filter.activeRange.min, filter.activeRange.max]
            : undefined,
      })),
    );

    // create base payload used by both product list and search result events
    const basePayload: GlycerinProductListBasePayload = {
      // criteo products are added first because they are displayed on top of the regular products if available
      tiles: [...sponsoredProductTiles, ...regularProductTiles],
      listType: displayMode,
      pageNumber: pageNumber ?? 1,
      sortMethod: mapSortingMethod(sortingKey),
      totalSize: result.summary.totalResultCount,
      filters,
    };

    switch (result.__typename) {
      case 'CategoryResult': {
        dispatchGTMEvent<GTMEventGlycerinDisplayProductList>({
          event: 'DisplayProductList',
          DisplayProductListData: {
            ...basePayload,
            name: result.categories.find((x) => x.isSelectedExact)?.name ?? '',
            categories: result.breadcrumbs.map((breadcrumb) => breadcrumb.name),
          },
        });

        break;
      }
      case 'SuccessfulSearchResult': {
        // having a mutable variable is even worse than a double ternary, fingers crossed for https://github.com/tc39/proposal-pattern-matching
        // eslint-disable-next-line no-nested-ternary
        const searchType =
          (suggestMarker && `${suggestMarker}-suggest`) || (historyMarker && 'history') || 'normal';

        const searchTermUtilized =
          (result.recoveryStrategy?.__typename === 'DeepQuery' &&
            result.recoveryStrategy.alternateQuery) ||
          (result.recoveryStrategy?.__typename === 'QueryReduction' &&
            result.recoveryStrategy.usedQuery) ||
          searchquery ||
          '';

        dispatchGTMEvent<GTMEventGlycerinDisplaySearchResult>({
          event: 'DisplaySearchResult',
          DisplaySearchResultData: {
            ...basePayload,
            searchTermSubmitted: searchquery ?? '',
            searchTermUtilized,
            algorithm: result.recoveryStrategy?.__typename ?? 'Default',
            searchType,
          },
        });

        break;
      }
      default:
        ensureExhaustiveCases(result);
    }
  };

  return { dispatchGlycerinListingsEvent };
};
